import { TrackedInputCheckbox } from '@objectiv/tracker-react';
import clsx from 'clsx';
import { forwardRef, useCallback, useRef } from 'react';

import { headingXxxs, textSm } from '../../typography';
import { ActionButton } from '../ActionButton';
import { SummaryDetails } from '../SummaryDetails';
import {
  Card,
  CardChip,
  CardContentWithMiddledIcon,
  CardContentWithoutIcon,
  CardDescription,
  CardHeading,
  CardWithInput,
} from '../card-components';

import styles from './PackageCard.module.css';

type ContentProps = {
  icon?: React.ReactNode;
  heading: string | React.ReactNode;
  description: React.ReactNode;
  controlRow?: React.ReactNode;
};

type DetailsProps = {
  details: React.ReactNode;
  detailsToggleLabel: string;
  expandableTrackingId?: string;
};

const ControlRow = ({ children }: { children: React.ReactNode }) => (
  <div className={clsx(styles.controlRow, headingXxxs)}>{children}</div>
);

const PackageContent = ({ icon, heading, description, controlRow = null }: ContentProps) => (
  <>
    {icon ? (
      <CardContentWithMiddledIcon icon={icon}>
        <div className={styles.packageContent}>
          <CardHeading>{heading}</CardHeading>
          <CardDescription>{description}</CardDescription>
          {controlRow}
        </div>
      </CardContentWithMiddledIcon>
    ) : (
      <CardContentWithoutIcon>
        <div className={styles.packageContent}>
          <CardHeading>{heading}</CardHeading>
          <CardDescription>{description}</CardDescription>
          {controlRow}
        </div>
      </CardContentWithoutIcon>
    )}
  </>
);

const PackageContentWithDetails = ({
  details,
  detailsToggleLabel,
  expandableTrackingId = 'readMore',
  controlRow,
  ...contentProps
}: ContentProps & DetailsProps) => {
  const ref = useRef<HTMLButtonElement>(null);
  const { icon, ...restProps } = contentProps;

  return (
    <SummaryDetails
      trackingId={expandableTrackingId}
      icon={icon}
      summary={
        <PackageContent
          {...restProps}
          controlRow={
            <div className={styles.controlRowWithDetailsToggle}>
              <button type='button' className={clsx(textSm, styles.detailsLink)} ref={ref}>
                {detailsToggleLabel}
              </button>
              {controlRow}
            </div>
          }
        />
      }
      details={<div className={clsx(styles.detailsContainer, textSm)}>{details}</div>}
      detailsRef={ref}
    />
  );
};

type ContentWithOrWithouDetailsProps = ContentProps | (ContentProps & DetailsProps);

const PackageContentWithOrWithoutDetails = (props: ContentWithOrWithouDetailsProps) => {
  if ('details' in props) {
    return <PackageContentWithDetails {...props} />;
  }

  return <PackageContent {...props} />;
};

type InputProps = Pick<React.ComponentProps<'input'>, 'name' | 'onChange' | 'onBlur' | 'id' | 'checked'> & {
  Input?: React.ComponentProps<typeof CardWithInput>['Input'];
  deltaPrice?: React.ReactNode;
  trackingId?: string;
  'data-testid'?: string;
  isLoading?: boolean;
};

export type CommonPackageCardProps =
  | Omit<ContentProps, 'controlRowItems'>
  | (Omit<ContentProps, 'controlRowItems'> & DetailsProps);

type PackageCardIncludedProps = { className?: string; includedLabel?: string } & CommonPackageCardProps;

const PackageCardIncluded = ({ className, includedLabel, ...props }: PackageCardIncludedProps) => {
  const included = includedLabel === undefined ? null : <CardChip>{includedLabel}</CardChip>;

  return (
    <Card className={clsx(className)}>
      <PackageContentWithOrWithoutDetails {...props} controlRow={<ControlRow>{included}</ControlRow>} />
    </Card>
  );
};

type PackageCardInputProps = { className?: string } & InputProps & CommonPackageCardProps;

const PackageCardInput = forwardRef<HTMLInputElement, PackageCardInputProps>(
  (
    { className, name, onChange, onBlur, checked, isLoading = false, deltaPrice, 'data-testid': testId, ...props },
    ref,
  ) => {
    const id = props.id ?? name;
    const trackingId = props.trackingId ?? id;
    const MemoizedInput = useCallback(
      (props: React.ComponentProps<typeof TrackedInputCheckbox>) => (
        <TrackedInputCheckbox
          {...{ ...props, name, onChange, onBlur, id, checked }}
          objectiv={{ id: trackingId, trackValue: true }}
          ref={ref}
        />
      ),
      [name, onChange, onBlur, id, checked, trackingId, ref],
    );

    return (
      <CardWithInput className={className} Input={MemoizedInput}>
        <PackageContentWithOrWithoutDetails
          {...props}
          controlRow={
            <ControlRow>
              {deltaPrice}
              {<ActionButton Input={() => null} id={id} loading={isLoading} data-testid={testId} />}
            </ControlRow>
          }
        />
      </CardWithInput>
    );
  },
);

export { PackageCardIncluded, PackageCardInput };
